// =======================================================
// ADMIN: Premium User Cleanup Panel
// =======================================================
// Add this to your Admin panel or create a new route
// /admin/cleanup or add to existing AdminDashboard

import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Icon } from '@iconify/react';

const PremiumCleanup = () => {
    const [loading, setLoading] = useState(false);
    const [auditResult, setAuditResult] = useState(null);
    const [cleanupResult, setCleanupResult] = useState(null);
    const [error, setError] = useState(null);

    const functions = getFunctions();

    // 1. RUN AUDIT (Safe - Read Only)
    const runAudit = async () => {
        setLoading(true);
        setError(null);
        setAuditResult(null);
        
        try {
            const auditPremiumUsers = httpsCallable(functions, 'auditPremiumUsers');
            const result = await auditPremiumUsers();
            setAuditResult(result.data);
            console.log('Audit Result:', result.data);
        } catch (err) {
            setError(err.message);
            console.error('Audit failed:', err);
        } finally {
            setLoading(false);
        }
    };

    // 2. RUN CLEANUP (DRY RUN - Preview Only)
    const runCleanupDryRun = async () => {
        setLoading(true);
        setError(null);
        setCleanupResult(null);
        
        try {
            const cleanupGhostPremium = httpsCallable(functions, 'cleanupGhostPremium');
            const result = await cleanupGhostPremium({ dryRun: true });
            setCleanupResult(result.data);
            console.log('Dry Run Result:', result.data);
        } catch (err) {
            setError(err.message);
            console.error('Dry run failed:', err);
        } finally {
            setLoading(false);
        }
    };

    // 3. EXECUTE CLEANUP (REAL - Deactivates Users!)
    const runCleanupForReal = async () => {
        if (!window.confirm('⚠️ This will DEACTIVATE all ghost premium users!\n\nAre you sure?')) {
            return;
        }
        if (!window.confirm('⚠️ FINAL WARNING!\n\nThis action cannot be undone. Continue?')) {
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const cleanupGhostPremium = httpsCallable(functions, 'cleanupGhostPremium');
            const result = await cleanupGhostPremium({ dryRun: false });
            setCleanupResult(result.data);
            console.log('Cleanup Result:', result.data);
            alert(`✅ Deactivated ${result.data.summary.deactivated} ghost users!`);
        } catch (err) {
            setError(err.message);
            console.error('Cleanup failed:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <h1 style={{ color: '#e2e8f0', marginBottom: '8px' }}>
                <Icon icon="solar:shield-warning-bold" style={{ marginRight: '8px' }} />
                Premium User Cleanup
            </h1>
            <p style={{ color: '#94a3b8', marginBottom: '24px' }}>
                Identify and remove ghost premium users who shouldn't have access.
            </p>

            {/* Error Display */}
            {error && (
                <div style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid #ef4444',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '20px',
                    color: '#f87171'
                }}>
                    <Icon icon="solar:danger-triangle-bold" style={{ marginRight: '8px' }} />
                    {error}
                </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <button
                    onClick={runAudit}
                    disabled={loading}
                    style={{
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#3b82f6',
                        color: 'white',
                        fontWeight: '600',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.7 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <Icon icon={loading ? "solar:spinner-bold" : "solar:magnifer-bold"} 
                          style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                    1. Run Audit (Safe)
                </button>

                <button
                    onClick={runCleanupDryRun}
                    disabled={loading}
                    style={{
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: '1px solid #f59e0b',
                        background: 'transparent',
                        color: '#f59e0b',
                        fontWeight: '600',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.7 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <Icon icon="solar:eye-bold" />
                    2. Preview Cleanup (Dry Run)
                </button>

                <button
                    onClick={runCleanupForReal}
                    disabled={loading || !cleanupResult}
                    style={{
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#ef4444',
                        color: 'white',
                        fontWeight: '600',
                        cursor: (loading || !cleanupResult) ? 'not-allowed' : 'pointer',
                        opacity: (loading || !cleanupResult) ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <Icon icon="solar:trash-bin-trash-bold" />
                    3. Execute Cleanup (DANGER)
                </button>
            </div>

            {/* Audit Results */}
            {auditResult && (
                <div style={{
                    background: 'rgba(30, 41, 59, 0.8)',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px'
                }}>
                    <h2 style={{ color: '#e2e8f0', marginBottom: '16px' }}>
                        📊 Audit Results
                    </h2>
                    
                    {/* Summary Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                        <div style={{ background: 'rgba(59, 130, 246, 0.1)', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#60a5fa' }}>
                                {auditResult.summary.totalPremiumUsers}
                            </div>
                            <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Total Premium</div>
                        </div>
                        <div style={{ background: 'rgba(34, 197, 94, 0.1)', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ade80' }}>
                                {auditResult.summary.legitimatePaid}
                            </div>
                            <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Legitimate</div>
                        </div>
                        <div style={{ background: 'rgba(250, 204, 21, 0.1)', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#facc15' }}>
                                {auditResult.summary.activeTrials}
                            </div>
                            <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Active Trials</div>
                        </div>
                        <div style={{ background: 'rgba(239, 68, 68, 0.1)', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f87171' }}>
                                {auditResult.summary.ghostUsers}
                            </div>
                            <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>👻 Ghosts</div>
                        </div>
                    </div>

                    {/* Ghost Users List */}
                    {auditResult.ghosts.length > 0 && (
                        <>
                            <h3 style={{ color: '#f87171', marginBottom: '12px' }}>
                                👻 Ghost Users ({auditResult.ghosts.length})
                            </h3>
                            <div style={{ maxHeight: '300px', overflowY: 'auto', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '12px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '1px solid #334155' }}>
                                            <th style={{ textAlign: 'left', padding: '8px', color: '#94a3b8' }}>Email</th>
                                            <th style={{ textAlign: 'left', padding: '8px', color: '#94a3b8' }}>Plan</th>
                                            <th style={{ textAlign: 'left', padding: '8px', color: '#94a3b8' }}>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {auditResult.ghosts.slice(0, 50).map((ghost, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid #1e293b' }}>
                                                <td style={{ padding: '8px', color: '#e2e8f0' }}>{ghost.email}</td>
                                                <td style={{ padding: '8px', color: '#94a3b8' }}>{ghost.plan}</td>
                                                <td style={{ padding: '8px', color: '#f87171', fontSize: '0.75rem' }}>{ghost.reason}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {auditResult.ghosts.length > 50 && (
                                    <p style={{ color: '#64748b', textAlign: 'center', marginTop: '12px' }}>
                                        ...and {auditResult.ghosts.length - 50} more
                                    </p>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Cleanup Results */}
            {cleanupResult && (
                <div style={{
                    background: cleanupResult.dryRun ? 'rgba(245, 158, 11, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                    border: `1px solid ${cleanupResult.dryRun ? '#f59e0b' : '#22c55e'}`,
                    borderRadius: '12px',
                    padding: '20px'
                }}>
                    <h2 style={{ color: cleanupResult.dryRun ? '#f59e0b' : '#22c55e', marginBottom: '16px' }}>
                        {cleanupResult.dryRun ? '👁️ Dry Run Preview' : '✅ Cleanup Complete'}
                    </h2>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f87171' }}>
                                {cleanupResult.summary.ghostsFound}
                            </div>
                            <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Ghosts Found</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4ade80' }}>
                                {cleanupResult.summary.legitimateSkipped}
                            </div>
                            <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Skipped</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: cleanupResult.dryRun ? '#f59e0b' : '#22c55e' }}>
                                {cleanupResult.dryRun ? cleanupResult.summary.ghostsFound : cleanupResult.summary.deactivated}
                            </div>
                            <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                                {cleanupResult.dryRun ? 'Will Deactivate' : 'Deactivated'}
                            </div>
                        </div>
                    </div>

                    {cleanupResult.dryRun && cleanupResult.summary.ghostsFound > 0 && (
                        <p style={{ color: '#f59e0b', marginTop: '16px', textAlign: 'center' }}>
                            ⚠️ Click "Execute Cleanup" to deactivate these {cleanupResult.summary.ghostsFound} ghost users
                        </p>
                    )}
                </div>
            )}

            {/* Spinner Animation */}
            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default PremiumCleanup;